<script setup>
import { computed, ref } from "vue";
import { __ } from "@/utils";
import Modal from "@components/modal.vue";

const emit = defineEmits(["numberPlan"]);
const { plan, selected, index, isRoaming } = defineProps({
  index: Number,
  plan: Object,
  selected: Boolean,
  isRoaming: {
    type: Boolean,
    default: false,
  },
});

const isActive = ref(false);
const features = ref(plan?.features);
const availableGB = ref(plan?.available_gigabyte);
const price = ref(plan?.montly_prices?.[0]?.price);
const popupText = ref(plan?.popup_text);
const recommended = ref(plan?.is_recommended);
const roamingUsage = ref('');

if(isRoaming){
  price.value = plan.price
  availableGB.value =  plan?.volumes?.[0]?.volume + plan?.volumes?.[0]?.unit.toUpperCase();

  const usageTypes = 
    {
      'data' : __('Data only'),
      'call' : __('Talking and texting'),
      'data_calls' : __('Surfing and talking'),
    };

  const volumes = plan?.volumes.map(item => item.usage_type);
  if(volumes.length === 1){
    roamingUsage.value = usageTypes['data'];
  } else if(volumes.length === 2){
    roamingUsage.value = usageTypes['call'];
  } else {
    roamingUsage.value = usageTypes['data_calls'];
  }

  // features
  const durationUnit = 
    {
      'days' : __('days'),
      'weeks' : __('weeks'),
      'months' : __('months'),
    };

  const durationFeatureText = __( 'package validity') + ':' + plan?.duration + ' ' + durationUnit[plan?.duration_unit];

  const planUnits = 
    {
      'minutes' : __('minutes'),
      'sms' : __('SMS')
    };

  const includedInPlan = plan?.volumes.map(item => {
    if(item.usage_type === 'sms') return item.volume + planUnits[item.usage_type];
    else if(item.usage_type === 'call') return item.volume + (planUnits[item.unit] ? planUnits[item.unit] : item.unit);
    else return `\u200F${item.volume + item.unit.toUpperCase()}\u200F`
  }).join(', ');
  features.value = [
    {'featured' : durationFeatureText},
    {'featured' : __('The program includes')+ ':' + includedInPlan},
    // {'featured' : __('the price of the package') + ':' + '₪' + price.value},
  ];

}

const toggleIsActive = () => {
  isActive.value = !isActive.value;
};

const handleClick = () => {
  if (isRoaming) return;
  isActive.value = false;
  emit("numberPlan", plan);
};

const title = computed(() => plan?.title?.replace(/\*([^*]+)\*/g, '<strong><em>$1</em></strong>'));

const handleTitle = (text) => text.replace('MINUTES', '');
</script>
<template>
  <div
    class="tile-plan"
    :class="{
      'is-active': isActive,
      'tile-plan--border': selected,
      'tile-plan--small': isRoaming,
	    'tile-plan__3-per-row': !isRoaming,
      'tile-plan--alt': plan.alt_heading,
    }"
  >
    <!-- <img
      :src="`${theme_path.url}/resources/images/svg/selected.svg`"
      alt="telecom icon"
      class="tile__selected"
      v-if="selected && !isRoaming"
    /> -->

    <slot name="badge" v-if="recommended" />

    <!-- TODO: requested to be hidden for now -->
    <div class="tile-plan__recommended" v-if="recommended && !isRoaming && false">
      {{ __("Recommended package") }}
    </div>
    <a href="#" class="tile__link" @click.prevent="handleClick">
      <header class="tile__head">
        <strong class="label">
          <i v-if="isRoaming && plan?.alpha2" class="tile__head-icon">
            <img :src="`https://flagcdn.com/w40/${plan.alpha2.toLowerCase()}.png`" alt="">
          </i>
          
          <span v-if="!isRoaming" v-html="title" />
          <span v-if="isRoaming && plan?.name_heb">{{ plan.name_heb }}</span>
        </strong>

        <div class="tile__title" v-if="availableGB">
          <strong>
            {{ handleTitle(availableGB) }}
          </strong>
          <p v-if="isRoaming">
            {{ roamingUsage }}
          </p>
        </div>
        <!-- /.tile__title -->
      </header>

      <div class="tile__body">
        <div class="tile__price" v-if="price">
          <strong>{{ Number(price) }}</strong>

          <span>
            <em>₪</em>
            בלבד!
          </span>
        </div>
        <div class="tile__list" v-if="features">
          <ul>
            <li
              v-for="(feature, featuredIndex) in features" :index="featuredIndex" :key="featuredIndex"
              :class="{'no-checkmark' : feature.add_gradient_styles}"
            >
              <span
                v-html="feature.featured"
                :class="{'gradient' : feature.add_gradient_styles}"
              >
            </span>
            </li>
          </ul>
        </div>
      </div>
    </a>

    <a
      href="#"
      class="btn btn--black btn--arrows btn--fullwidth"
      type="button"
      @click.prevent="handleClick"
      :style="{ background: selected ? '#4EA8DA!important' : '' }"
      v-if="!$slots.actions"
    >
      {{ __("Choose") }} </a
    ><!-- /.link -->
    <slot name="actions" v-bind="plan" v-if="$slots.actions" />

    <div class="tile__foot tile__foot--step tile__foot_margin-top">
      <a href="#" class="link-icon link-icon--small" @click.prevent="toggleIsActive">
        <img :src="`${theme_path.url}/resources/images/ico-tooltip.svg`" />
        {{ __("for package details") }}
      </a>
    </div>
  <Modal :isActive="isActive" @close="isActive = false" :class="{'popup--alt-heading': plan.alt_heading}">
    <template #head>
      <p v-if="!isRoaming">{{ plan.title }}</p>
      <p v-if="isRoaming && plan?.name_heb">
        {{ __('Package details for') }}-{{ plan.name_heb }}
      </p>

      <h2 v-if="availableGB" class="gradient">
        {{ handleTitle(availableGB) }}
      </h2>
      <p v-if="isRoaming" class="gradient">
        {{ roamingUsage }}
      </p>
    </template>
    <template #body>
      <div class="popup__body-right" v-html="popupText"></div>
      <div class="tile__list popup__body-right" v-if="features && isRoaming">
        {{ __('Program Highlights') }}
          <div v-for="(feature, featuredIndex) in features" :index="featuredIndex" :key="featuredIndex">
            <span dir="rtl" v-html="feature.featured"></span>
          </div>
          <slot name="popup_body" v-bind="plan" />
      </div>
    </template>
    <template #foot>
      <a href="#" class="btn" type="button" @click.prevent="toggleIsActive">
        {{ __("Accept") }} </a
      ><!-- /.btn -->
    </template>
  </Modal>
  </div>
</template>
